import React, { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { Feature } from 'geojson';
import { SearchBar } from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';
import {
  MetaLayerDefinition,
  SearchResultFlat,
  getSearchResultLocation,
  useLayers,
  useSearchResults,
} from '@agissc/ktag-map';
import * as turf from '@turf/turf';
import useAppState from '../../AppStore';
import { getFilteredTopics } from '../mapContent/topics/filterTopicLayers';
import useDisplaySize from '../../useDisplaySize';
import { useTracking } from '../../utils/tracking/TrackingContext';
import Scrollable from '../../components/scrollable/Scrollable';
import { ToolNames } from '../ToolNames';
import useToggleTopicLayer from '../hooks/useToggleTopicLayer';
import SearchLayer from '../../components/searchresultitem/SearchLayer';
import SearchLocation from '../../components/searchresultitem/SearchLocation';
import aargauShape from '../../assets/geojson/aargau.json';
import { useDebouncedState } from '../../utils/useDebouncedState';
import { useFavorites } from '../hooks/useFavorites';
import SearchResultItemPlaceholder from '../../components/searchresultitem/SearchResultItemPlaceholder';
import isMatchingSearchResult from './api/isMatchingSearchResult';
import SearchInfoBoxNotInAG from './SearchInfoBoxNotInAG';

const isSearchResultFlat = (
  value: SearchResultFlat | MetaLayerDefinition,
): value is SearchResultFlat => 'origin' in value;

const isMetaLayerDefinition = (
  value: SearchResultFlat | MetaLayerDefinition,
): value is MetaLayerDefinition => 'metadataUrl' in value;

const Search = () => {
  const { t } = useTranslation('agisviewer');
  const { isSmallScreen } = useDisplaySize();
  const { Tracking } = useTracking();
  const { addTopicLayer, removeTopicLayer } = useToggleTopicLayer();
  const searchRef = useRef<HTMLInputElement>(null);
  const setActiveTool = useAppState((state) => state.setActiveTool);
  const searchText = useAppState((state) => state.searchText);
  const [searchTextLocal, setSearchTextLocal] = useState(searchText);
  const setSearchText = useAppState((state) => state.setSearchText);
  const debounceSearchText = useCallback(debounce(setSearchText, 300), [setSearchText]);
  const selectedSearchResult = useAppState((state) => state.selectedSearchResult);
  const setSelectedSearchResult = useAppState((state) => state.setSelectedSearchResult);
  const selectedTopicLayers = useAppState((state) => state.selectedTopicLayers);
  const setNewAddedTopicLayer = useAppState((state) => state.setNewAddedTopicLayer);
  const location = useAppState((state) => state.location);
  const setLocation = useAppState((state) => state.setLocation);
  const [displaySearchInfoBoxNotInAG, setdisplaySearchInfoBoxNotInAG] =
    useState<boolean>(false);

  const { data: searchResults = [], isLoading: searchLoading } = useSearchResults();
  const { data: layerData = {}, isLoading: layerIsLoading } = useLayers();
  const isLoading = searchLoading || layerIsLoading;

  const layerList = getFilteredTopics(layerData, searchText);

  const { getUserFavorites, toggleUserFavorite } = useFavorites();
  const favorites = getUserFavorites();

  const isFavorite = (id: any) => id in favorites;
  const isSelectedLayer = (id: string) => selectedTopicLayers.includes(id);

  const isSelectedLocation = (item: SearchResultFlat) =>
    isMatchingSearchResult(item, selectedSearchResult);

  const favoriteEntries = Object.values(favorites);

  const [, setPreviewLayerDebounce] = useDebouncedState(
    useAppState((state) => state.previewLayer),
    useAppState((state) => state.setPreviewLayer),
    300,
  );

  const handleToggleFavorite = (
    topicLayer: SearchResultFlat | MetaLayerDefinition,
    trackerTrigger: string,
  ) => {
    if (isFavorite(topicLayer.id)) {
      Tracking.Favorites.removeFavorite(topicLayer.id, 'Search', trackerTrigger);
    } else {
      Tracking.Favorites.addFavorite(topicLayer.id, 'Search', trackerTrigger);
    }
    toggleUserFavorite(topicLayer);
  };

  const handleToggleTopicLayer = async (
    topicLayer: MetaLayerDefinition,
    trackerTrigger: string,
    closeView: boolean = false,
  ) => {
    if (isSelectedLayer(topicLayer.id)) {
      Tracking.Topics.removeTopic(
        topicLayer.id,
        selectedTopicLayers.length - 1,
        'search',
        trackerTrigger,
      );
      removeTopicLayer(topicLayer);
    } else {
      Tracking.Topics.addTopic(
        topicLayer.id,
        selectedTopicLayers.length + 1,
        'search',
        trackerTrigger,
      );
      addTopicLayer(topicLayer);
    }

    setNewAddedTopicLayer(topicLayer.id);
    if (isSmallScreen) {
      setActiveTool(ToolNames.identify);
    } else if (closeView) {
      setActiveTool(ToolNames.layerSelection);
    }
  };

  useEffect(() => {
    searchRef.current?.focus();
  }, [favorites, selectedTopicLayers, layerList, searchLoading]);

  const onClearHandler = () => {
    Tracking.Search.clearSearchbox();
    setSelectedSearchResult(null);
  };

  useEffect(() => {
    if (!searchRef.current) return undefined;

    const element = document.querySelector(
      '.searchbar__clear-button',
    ) as HTMLButtonElement | null;

    if (!element) return undefined;

    element.addEventListener('click', onClearHandler);

    return () => {
      element.removeEventListener('click', onClearHandler);
    };
  }, [searchRef]);

  useEffect(() => {
    debounceSearchText(searchTextLocal);
    if (searchTextLocal.length === 0) {
      onClearHandler();
    }
  }, [searchTextLocal]);

  const selectSearchResultById = (id: string) => {
    const items = searchResults.length
      ? searchResults
      : favoriteEntries.filter(isSearchResultFlat);

    const item = items.find((x) => x.id === id);
    if (!item) return;

    const input = !isSelectedLocation(item) ? item : null;

    if (input) {
      Tracking.Search.selectSearchResult(searchText, item.origin ?? 'unknown');
    } else {
      Tracking.Search.unSelectSearchResult(searchText, item.origin ?? 'unknown');
    }

    setSelectedSearchResult(input);

    if (isSmallScreen) {
      setActiveTool(ToolNames.identify);
    }

    if (!input) return;

    const feature: GeoJSON.Feature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [item.x, item.y],
      },
      properties: {},
    };

    const isWithinAG = !turf.booleanDisjoint(
      feature,
      aargauShape.features[0] as Feature<any>,
    );

    setdisplaySearchInfoBoxNotInAG(!isWithinAG);
  };

  const goToLocation = (item: SearchResultFlat) => {
    const newLocation = getSearchResultLocation(item, location);
    if (newLocation) setLocation(newLocation);
  };

  const handleSearchInfoBoxNotInAgOnClose = () => {
    setdisplaySearchInfoBoxNotInAG(false);
  };

  return (
    <>
      {displaySearchInfoBoxNotInAG && (
        <SearchInfoBoxNotInAG onClose={handleSearchInfoBoxNotInAgOnClose} />
      )}
      <div className="tooldialog__wrapper">
        {!isSmallScreen && (
          <div className="search__inputbar">
            <SearchBar
              ref={searchRef}
              placeholder={t('searchbar_placeholder')}
              search={searchTextLocal}
              setSearch={setSearchTextLocal}
              small
            />
          </div>
        )}

        <Scrollable scrollAdditionClassName="scrollable__wrapper">
          {!searchText ? (
            <>
              {favoriteEntries.length > 0 && (
                <h2 className="search__result-title">{t('favorites')}</h2>
              )}

              {favoriteEntries.map((item) => {
                return isMetaLayerDefinition(item) ? (
                  <SearchLayer
                    key={item.id}
                    layer={item}
                    markedText={searchText}
                    isSelected={isSelectedLayer(item.id)}
                    isFavorite={isFavorite(item.id)}
                    onClick={() => handleToggleTopicLayer(item, 'listitem_favorites')}
                    onToggleFavorite={() => handleToggleFavorite(item, 'layer')}
                    onMouseEnter={() => setPreviewLayerDebounce(item.id)}
                    onMouseLeave={() => setPreviewLayerDebounce(undefined)}
                  />
                ) : (
                  <SearchLocation
                    key={item.id}
                    location={item as SearchResultFlat}
                    markedText={searchText}
                    isSelected={isSelectedLocation(item)}
                    isFavorite={isFavorite(item.id)}
                    onClick={() => selectSearchResultById(item.id)}
                    onIconClick={() => goToLocation(item as SearchResultFlat)}
                    onToggleFavorite={() => handleToggleFavorite(item, 'location')}
                  />
                );
              })}
            </>
          ) : (
            <>
              {searchResults.length > 0 && (
                <>
                  <h2 className="search__result-title">
                    {t('search_results_subtitle_location')}
                  </h2>
                  {isLoading && <SearchResultItemPlaceholder />}
                </>
              )}

              {searchResults.map((item) => (
                <SearchLocation
                  key={item.id}
                  location={item}
                  markedText={searchText}
                  isSelected={isSelectedLocation(item)}
                  isFavorite={isFavorite(item.id)}
                  onClick={() => selectSearchResultById(item.id)}
                  onIconClick={() => goToLocation(item)}
                  onToggleFavorite={() => handleToggleFavorite(item, 'location')}
                />
              ))}

              {layerList.length > 0 && (
                <h2 className="search__result-title">
                  {t('search_results_subtitle_map')}
                </h2>
              )}

              {layerList.map((item) => (
                <SearchLayer
                  key={item.id}
                  layer={item}
                  markedText={searchText}
                  isSelected={isSelectedLayer(item.id)}
                  isFavorite={isFavorite(item.id)}
                  onClick={() => handleToggleTopicLayer(item, 'listitem', true)}
                  onIconClick={() => handleToggleTopicLayer(item, 'plusminusicon')}
                  onToggleFavorite={() => handleToggleFavorite(item, 'layer')}
                  onMouseEnter={() => setPreviewLayerDebounce(item.id)}
                  onMouseLeave={() => setPreviewLayerDebounce(undefined)}
                />
              ))}
            </>
          )}
        </Scrollable>
      </div>
    </>
  );
};

export default Search;
